import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Plus, Sparkles, Trash2, Users, Pencil } from "lucide-react";
import type { CadenceName, ContactSort } from "@hub/shared";
import { Button, EmptyState, Field, IconButton, Input, Menu, Select, Spinner, Tabs, Textarea, toast, Modal } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useOpenContact, useSelection } from "@/app/navigation";
import { Page } from "@/app/layout/Page";
import { CONTACT_DRAG_TYPE } from "@/app/layout/NavSidebar";
import { ContactRowView } from "@/features/people/ContactRowView";
import { SearchBox } from "@/features/people/SearchBox";
import { FilterPanel, useGroupNames } from "@/features/people/FilterPanel";
import { ChipRow, useQueryChips } from "@/features/people/ChipRow";
import { SORTS, clearPart, composeQuery, emptyFilter, filterParts, type PeopleFilter } from "@/features/people/filter";
import { NoteCard } from "@/features/contact/ContactSections";
import { NoteEditor } from "@/features/contact/NoteEditor";
import { ConfirmModal } from "@/features/contact/ConfirmModal";
import { downloadText } from "@/features/settings/download";
import { GroupForm, type GroupDraft } from "./GroupForm";
import { ContactChip } from "@/features/people/ContactChip";
import s from "../shared.module.css";

type Tab = "members" | "notes" | "highlights" | "settings";

export function GroupPage({ tab: initialTab }: { tab?: Tab } = {}) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const open = useOpenContact();
  const sidebarId = useSelection((st) => st.sidebarContactId);
  const [tab, setTab] = useState<Tab>(initialTab ?? "members");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<ContactSort>("name");
  const [panel, setPanel] = useState<PeopleFilter>(() => emptyFilter());
  const filter = useMemo(() => ({ ...panel, sort }), [panel, sort]);
  const groupNames = useGroupNames();
  const parts = useMemo(() => filterParts(filter, groupNames), [filter, groupNames]);
  const composed = composeQuery(parts.map((p) => p.clause).join(" "), query);
  const [editingNote, setEditingNote] = useState<"new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const group = useEngineQuery("getGroup", { groupId: id });
  const members = useEngineQuery("getContactsInGroup", { groupId: id, query: composed || undefined, sort, direction: sort === "name" ? "asc" : "desc", limit: 500 }, { placeholderData: (p) => p });
  const chips = useQueryChips({ parts, typed: query, onClearPart: (key) => setPanel((f) => clearPart(f, key)), onReset: () => setPanel(emptyFilter()), onTyped: setQuery });
  const highlights = useEngineQuery("getGroupHighlights", { groupId: id }, { enabled: tab === "highlights" });
  const addSearch = useEngineQuery("queryContacts", { query: addQuery, limit: 8 }, { enabled: adding && addQuery.trim().length > 0 });
  const update = useEngineMutation("updateGroup", { invalidate: ["getGroup", "listGroups", "getContactsInGroup"] });
  const remove = useEngineMutation("deleteGroup", { invalidate: ["listGroups", "queryContacts"] });
  const add = useEngineMutation("addContactsToGroup", { invalidate: ["getGroup", "listGroups", "getContactsInGroup", "queryContacts", "getContact"] });
  const removeMembers = useEngineMutation("removeContactsFromGroup", { invalidate: ["getGroup", "listGroups", "getContactsInGroup", "queryContacts", "getContact"] });
  const exportCsv = useEngineMutation("exportGroupCsv", { invalidate: false });
  const deleteNote = useEngineMutation("deleteNote", { invalidate: ["getGroup", "getAllNotes"] });
  const star = useEngineMutation("setContactStarred", { invalidate: ["getContactsInGroup", "listGroups", "getGroup", "queryContacts"] });
  useEffect(() => setTab(initialTab ?? "members"), [id, initialTab]);

  if (group.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner /></div>;
  if (!group.data) return <EmptyState title="This group no longer exists" action={<Button onClick={() => navigate("/people")}>Back to people</Button>} />;
  const g = group.data;
  const rows = members.data?.items ?? [];

  return (
    <Page
      flush
      title={
        <span className="u-row u-grow" style={{ minWidth: 0 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: g.color ?? "var(--text-tertiary)", flex: "none" }} />
          <h1 style={{ margin: 0, fontSize: 16, fontWeight: 650 }} className="u-truncate">{g.name}</h1>
          <span className="u-small u-muted">{g.memberCount} {g.memberCount === 1 ? "person" : "people"}{g.kind === "smart" ? ", smart group" : ""}</span>
        </span>
      }
      actions={
        <>
          {g.kind === "static" && !g.isInternal && <Button size="sm" icon={<Plus size={13} />} onClick={() => setAdding(true)}>Add people</Button>}
          <Menu
            align="right"
            trigger={<Button size="sm" variant="outline">More</Button>}
            items={[
              { id: "csv", label: "Download CSV", icon: <Download size={13} />, onSelect: () => exportCsv.mutate({ groupId: g.id }, { onSuccess: (r) => downloadText(`${g.slug ?? "group"}.csv`, r.csv, "text/csv") }) },
              { id: "hl", label: "Highlights", icon: <Sparkles size={13} />, onSelect: () => setTab("highlights") },
              { id: "edit", label: "Edit group", icon: <Pencil size={13} />, onSelect: () => setTab("settings") },
              { id: "d", label: "", divider: true },
              { id: "del", label: "Delete group", icon: <Trash2 size={13} />, danger: true, disabled: g.isInternal, onSelect: () => setConfirmDelete(true) },
            ]}
          />
        </>
      }
    >
      <div style={{ padding: "0 20px" }}>
        {g.description && <p className="u-muted" style={{ margin: "10px 0 0" }}>{g.description}</p>}
        <div style={{ marginTop: 8 }}>
          <Tabs<Tab> value={tab} onChange={setTab} tabs={[{ id: "members", label: "Members" }, { id: "notes", label: `Notes${g.notes.length ? ` (${g.notes.length})` : ""}` }, { id: "highlights", label: "Highlights" }, { id: "settings", label: "Settings" }]} />
        </div>
      </div>
      {tab === "members" && (
        <>
          <ChipRow chips={chips.chips} onRemove={chips.remove} onClear={chips.clear} />
          <div className={s.toolbar}>
            <SearchBox value={query} onChange={setQuery} showHints={false} showParsed={false} placeholder="Filter members" />
            <Select value={sort} onChange={(e) => setSort(e.target.value as ContactSort)} style={{ width: 180 }} aria-label="Sort">
              {SORTS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </Select>
            <FilterPanel filter={filter} onChange={(f) => { setSort(f.sort); setPanel({ ...f, sort: "name" }); }} count={members.data?.total ?? rows.length} />
          </div>
          <div
            style={{ flex: 1, overflowY: "auto" }}
            onDragOver={g.kind === "static" ? (e) => e.dataTransfer.types.includes(CONTACT_DRAG_TYPE) && e.preventDefault() : undefined}
            onDrop={g.kind === "static" ? (e) => { const raw = e.dataTransfer.getData(CONTACT_DRAG_TYPE); if (raw) add.mutate({ groupId: g.id, contactIds: raw.split(",") }); } : undefined}
          >
            {members.isSuccess && rows.length === 0 && (
              <EmptyState icon={<Users size={26} className="u-muted" />} title={g.kind === "smart" ? "Nobody matches this filter yet" : "No members yet"}>
                {g.kind === "smart" ? "Smart groups fill themselves. Adjust the filter in Settings." : "Add people from the button above, or drag them from the People list onto this group in the sidebar."}
              </EmptyState>
            )}
            {rows.map((r) => (
              <div key={r.contactId} className="u-row" style={{ gap: 0 }}>
                <div className="u-grow">
                  <ContactRowView row={r} showCheckbox={false} active={sidebarId === r.contactId} onOpen={() => open(r.contactId)} onStar={() => star.mutate({ contactId: r.contactId, starred: !r.isStarred })} onDragStart={(e) => e.dataTransfer.setData(CONTACT_DRAG_TYPE, r.contactId)} />
                </div>
                {g.kind === "static" && (
                  <IconButton label="Remove from group" onClick={() => (g.isInternal ? star.mutate({ contactId: r.contactId, starred: false }) : removeMembers.mutate({ groupId: g.id, contactIds: [r.contactId] }))} style={{ marginRight: 8 }}>
                    <Trash2 size={14} />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {tab === "notes" && (
        <div className={s.section} style={{ padding: "16px 20px", overflowY: "auto" }}>
          <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>Group notes</span>
            <Button size="sm" icon={<Plus size={13} />} onClick={() => setEditingNote("new")}>New note</Button>
          </div>
          {g.notes.length === 0 && <div className="u-muted u-small">Notes about the group as a whole, such as how you run it or what it is for.</div>}
          {g.notes.map((n) => <NoteCard key={n.id} note={n} onDelete={() => deleteNote.mutate({ noteId: n.id })} />)}
          <NoteEditor open={editingNote !== null} onClose={() => setEditingNote(null)} groupId={g.id} />
        </div>
      )}
      {tab === "highlights" && (
        <div style={{ padding: "16px 20px", overflowY: "auto" }}>
          {highlights.isLoading && <Spinner />}
          <ul className={s.bullets}>
            {(highlights.data ?? []).map((h, i) => (
              <li key={i}>
                {h.text}
                {h.contactIds && <span className="u-row" style={{ display: "inline-flex", gap: 4, marginLeft: 6 }}>{h.contactIds.slice(0, 6).map((cid) => <ContactChip key={cid} contactId={cid} />)}</span>}
              </li>
            ))}
          </ul>
          {highlights.isSuccess && highlights.data.length === 0 && <div className="u-muted u-small">Highlights appear once the group has members with some history.</div>}
        </div>
      )}
      {tab === "settings" && (
        <div style={{ padding: "16px 20px", overflowY: "auto", maxWidth: 640 }}>
          <GroupForm
            initial={{ name: g.name, description: g.description ?? "", kind: g.kind, filter: g.filter ?? "", color: g.color ?? "", reconnectCadence: g.reconnectCadence, parentId: g.parentId }}
            groupId={g.id}
            submitLabel="Save changes"
            loading={update.isPending}
            lockKind={g.isInternal}
            onSubmit={(d: GroupDraft) => update.mutate({ groupId: g.id, input: { name: d.name, description: d.description || null, kind: d.kind, filter: d.kind === "smart" ? d.filter : null, color: d.color || null, parentId: d.parentId, reconnectCadence: (d.reconnectCadence || null) as CadenceName | null } }, { onSuccess: () => { toast("Group saved", "success"); setTab("members"); } })}
          />
        </div>
      )}
      <Modal open={adding} onClose={() => setAdding(false)} title={`Add people to ${g.name}`}>
        <div className="u-col">
          <Input autoFocus placeholder="Search by name, company, anything" value={addQuery} onChange={(e) => setAddQuery(e.target.value)} />
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {(addSearch.data?.items ?? []).map((r) => (
              <ContactRowView key={r.contactId} row={r} showCheckbox={false} onOpen={() => add.mutate({ groupId: g.id, contactIds: [r.contactId] }, { onSuccess: () => toast(`Added ${r.displayName}`) })} />
            ))}
          </div>
        </div>
      </Modal>
      <ConfirmModal open={confirmDelete} title={`Delete ${g.name}?`} body="People in the group are not affected; only the group goes away." confirmLabel="Delete group" danger loading={remove.isPending} onClose={() => setConfirmDelete(false)} onConfirm={() => remove.mutate({ groupId: g.id }, { onSuccess: () => { toast("Group deleted"); navigate("/people"); } })} />
    </Page>
  );
}
